import {ChangeDetectionStrategy, Component, computed, effect, inject, signal,} from '@angular/core';
import {AlertController, ToastController} from '@ionic/angular/lazy';
import {LmdbAdminApiService} from '../../services/lmdb-admin-api.service';
import {LmdbServerOption} from '../../config/lmdb-cache-config';
import {LMDB_ADMIN_PERMISSIONS} from "../../constants/lmdb-admin-permission.constant";
import {AsyncPipe} from "@angular/common";
import {
  buildWebsiteCacheConfigFromDb,
  formatWebsiteCacheConfigLabel,
  WebsiteCacheStartConfigBody,
} from '../../constants/website-cache-start.constant';
import {HttpClientService} from "@app/lib/b2b-services/util/http-client.service";
import {environment} from "@src/environments/environment";
import {PermissionsModule} from "@app/lib/b2b-pipes/permissions/permissions.module";

@Component({
  selector: 'lib-lmdb-cache-page',
  templateUrl: './lmdb-cache-page.component.html',
  styleUrls: ['./lmdb-cache-page.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PermissionsModule,
    AsyncPipe,
  ]
})
export class LmdbCachePageComponent {
  private readonly api: LmdbAdminApiService = inject(LmdbAdminApiService);
  private readonly alertController: AlertController = inject(AlertController);
  private readonly toastController: ToastController = inject(ToastController);
  private readonly httpClientService: HttpClientService = inject(HttpClientService);

  protected readonly LMDB_ADMIN_PERMISSIONS = LMDB_ADMIN_PERMISSIONS;
  readonly servers: LmdbServerOption[] = environment.SERVER_OPTIONS;
  readonly selectedServer = this.api.selectedServerSignal;
  /** ARI shard roots from lmdb-cache-admin (`ARI_LMDB_SHARD_PATHS_JSON`, same as ari-processing). */
  readonly ariShardPickerOptions = computed(() => this.api.ariShardsSignal());
  readonly ariShardsLoaded = this.api.ariShardsLoadedSignal;
  readonly selectedAriShardPath = this.api.selectedAriShardPathSignal;
  readonly stores = this.api.storesSignal;
  readonly databases = this.api.databasesSignal;
  readonly keysResult = this.api.keysResultSignal;
  readonly selectedValue = this.api.selectedValueSignal;
  readonly storeStats = this.api.storeStatsSignal;
  readonly statistics = this.api.statisticsSignal;
  readonly loading = this.api.loadingSignal;
  readonly error = this.api.errorSignal;

  readonly selectedStoreId = signal<string | null>(null);
  readonly selectedDbName = signal<string | null>(null);
  readonly selectedKey = signal<string | null>(null);
  readonly keyPrefix = signal('');
  readonly keysOffset = signal(0);
  readonly keysLimit: number = 50;
  readonly startingCache = signal(false);

  readonly hasStores = computed(() => (this.stores()?.length ?? 0) > 0);
  readonly hasDatabases = computed(() => (this.databases()?.length ?? 0) > 0);
  readonly hasKeys = computed(() => this.keysList().length > 0);

  /**
   * For ARI with multi-shard env, require a node. If env has no shard map, use default `LMDB_ARI_PATH` root.
   * While shard list is loading, hide databases so we do not query the wrong root.
   */
  readonly ariDatabasesPanelEnabled = computed(() => {
    const id: string = this.selectedStoreId();
    if (id !== 'ari') {
      return true;
    }
    if (!this.api.ariShardsLoadedSignal()) {
      return false;
    }
    if (this.api.ariShardsSignal().length === 0) {
      return true;
    }
    const shard: string = this.selectedAriShardPath();
    return !!(shard && shard.length > 0);
  });

  /** Sum of key counts across DBs for the selected ARI shard (from store stats). */
  readonly ariNodeTotalKeys = computed(() => {
    const stats = this.storeStats();
    if (!stats?.databases?.length) {
      return 0;
    }
    return stats.databases.reduce((acc, d) => acc + (d.entryCount ?? 0), 0);
  });

  /** Keys as a string[] so we never iterate over a string (character-by-character). */
  readonly keysList = computed(() => {
    const k: string[] = this.keysResult()?.keys;
    return Array.isArray(k) ? k : [];
  });

  /** Key count for the selected database (from storeStats or databases with keyCount). */
  readonly selectedDbKeyCount = computed(() => {
    const stats = this.storeStats();
    const dbName = this.selectedDbName();
    if (stats?.databases && dbName) {
      const db = stats.databases.find((d) => d.name === dbName);
      return db?.entryCount ?? this.keysResult()?.total ?? 0;
    }
    const dbs = this.databases();
    const db = dbs?.find((d) => d.name === dbName);
    return db?.keyCount ?? this.keysResult()?.total ?? 0;
  });

  /** Pre-formatted store rows (avoids {@link formatCount} in `@for` / change detection). */
  readonly storesDisplayRows = computed(() => {
    return this.stores().map((store) => ({
      ...store,
      totalKeyCountFormatted:
        store.totalKeyCount !== undefined
          ? this.formatCount(store.totalKeyCount)
          : undefined,
    }));
  });

  /** ARI shard options with pre-formatted key counts for the node `<select>`. */
  readonly ariShardSelectRows = computed(() =>
    this.ariShardPickerOptions().map((opt) => ({
      ...opt,
      totalKeyCountFormatted: this.formatCount(opt.totalKeyCount),
    })),
  );

  readonly ariNodeTotalKeysFormatted = computed(() => this.formatCount(this.ariNodeTotalKeys()));

  readonly selectedDbKeyCountFormatted = computed(() => this.formatCount(this.selectedDbKeyCount()));

  /** Pretty-printed value for the value panel (empty when null/undefined — template keeps separate `@if`). */
  readonly selectedValueDisplay = computed(() => {
    const value: unknown = this.selectedValue();
    if (value === null || value === undefined) {
      return '';
    }
    return this.formatValue(value);
  });

  /** Database list with optional pre-formatted key counts for both database panels. */
  readonly databasesDisplayRows = computed(() => {
    const storeId = this.selectedStoreId();
    return this.databases().map((db) => {
      const cacheConfig = storeId
        ? buildWebsiteCacheConfigFromDb(storeId, db.name)
        : null;
      return {
        ...db,
        keyCountFormatted:
          db.keyCount !== undefined ? this.formatCount(db.keyCount) : undefined,
        cacheConfig,
        cacheConfigLabel: cacheConfig
          ? formatWebsiteCacheConfigLabel(cacheConfig)
          : null,
      };
    });
  });

  /** Statistics summary header cards; `null` when statistics have not been loaded. */
  readonly statisticsSummaryVm = computed(() => {
    const stats = this.statistics();
    if (!stats) {
      return null;
    }
    return {
      totalStoresFormatted: this.formatCount(stats.totalStores),
      totalDatabasesFormatted: this.formatCount(stats.totalDatabases),
      totalKeysFormatted: this.formatCount(stats.totalKeys),
      totalSizeFormatted: this.formatSize(stats.totalSizeBytes),
    };
  });

  /** One card per store in the statistics grid (status + formatted badge + DB rows). */
  readonly statisticsStoreCardsVm = computed(() => {
    const stats = this.statistics();
    if (!stats?.stores) {
      return [];
    }
    return stats.stores.map((store) => {
      const status = this.getStoreStatus(store);
      return {
        storeId: store.storeId,
        storeName: store.storeName,
        totalDatabases: store.totalDatabases,
        badgeKeysFormatted: this.formatCount(store.totalKeys),
        badgeSizeFormatted: this.formatSize(store.totalSizeBytes),
        isHealthy: status === 'healthy',
        isWarning: status === 'warning',
        isCritical: status === 'critical',
        statusLabel: this.getStoreStatusLabel(store),
        databases: store.databases.map((db) => ({
          name: db.name,
          keyCountFormatted: this.formatCount(db.keyCount),
          sizeFormatted: this.formatSize(db.sizeBytes),
        })),
      };
    });
  });

  /** Per-node ARI shard cards when `statistics.ariShards` is present. */
  readonly statisticsAriShardCardsVm = computed(() => {
    const stats = this.statistics();
    if (!stats?.ariShards?.length) {
      return [];
    }
    return stats.ariShards.map((shard, index) => {
      const status = this.getStoreStatus(shard);
      return {
        trackId: shard.shardId || shard.shardPath || `shard-${index}`,
        shardId: shard.shardId ?? '',
        shardPath: shard.shardPath,
        storeName: shard.storeName,
        totalDatabases: shard.totalDatabases,
        badgeKeysFormatted: this.formatCount(shard.totalKeys),
        badgeSizeFormatted: this.formatSize(shard.totalSizeBytes),
        isHealthy: status === 'healthy',
        isWarning: status === 'warning',
        isCritical: status === 'critical',
        statusLabel: this.getStoreStatusLabel(shard),
        databases: shard.databases.map((db) => ({
          name: db.name,
          keyCountFormatted: this.formatCount(db.keyCount),
          sizeFormatted: this.formatSize(db.sizeBytes),
        })),
      };
    });
  });

  /** Format number with comma separators. */
  formatCount(n: number | undefined): string {
    if (n === undefined || n === null) return '0';
    return n.toLocaleString();
  }

  /** Format bytes as KB / MB / GB. */
  formatSize(bytes: number | undefined): string {
    if (bytes === undefined || bytes === null) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  /** Status: healthy &lt; 70%, warning 70–90%, critical 90%+ of map size. */
  getStoreStatus(store: { totalSizeBytes: number; mapSizeLimitBytes: number }): 'healthy' | 'warning' | 'critical' {
    const limit: number = store.mapSizeLimitBytes;
    if (!limit || limit <= 0) return 'healthy';
    const pct = store.totalSizeBytes / limit;
    if (pct >= 0.9) return 'critical';
    if (pct >= 0.7) return 'warning';
    return 'healthy';
  }

  getStoreStatusLabel(store: { totalSizeBytes: number; mapSizeLimitBytes: number }): string {
    const s = this.getStoreStatus(store);
    if (s === 'healthy') return 'Healthy';
    if (s === 'warning') return 'Warning';
    return 'Critical';
  }

  /** Format value for display: pretty JSON if possible, otherwise stringified. */
  formatValue(value: unknown): string {
    if (value === null || value === undefined) return 'No value found or value is null.';
    if (typeof value === 'object' && value !== null && '_raw' in value) {
      const raw = value as { _raw?: boolean; format?: string; value?: string; error?: string; message?: string };
      if (raw.value !== undefined) return raw.value;
      if (raw.message) return raw.message;
      if (raw.error) return `Deserialization error: ${raw.error}`;
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  constructor() {
    effect(() => {
      const server: LmdbServerOption = this.selectedServer();
      if (server) {
        this.api.loadStores();
        this.api.loadStatistics();
      }
    });

    effect(() => {
      const sid = this.selectedStoreId();
      if (sid !== 'ari') {
        return;
      }
      if (!this.api.ariShardsLoadedSignal()) {
        return;
      }
      if (this.api.ariShardsSignal().length > 0) {
        return;
      }
      if (this.selectedAriShardPath()) {
        return;
      }
      this.api.loadDatabases('ari');
      this.api.loadStoreStats('ari');
    });
  }

  onServerChange(event: Event): void {
    const id: string = (event.target as HTMLSelectElement)?.value;
    const server: LmdbServerOption = this.servers.find((s: LmdbServerOption) => s.id === id);
    if (server) this.api.setSelectedServer(server);
  }

  selectStore(storeId: string): void {
    this.selectedStoreId.set(storeId);
    this.selectedDbName.set(null);
    this.selectedKey.set(null);
    this.api.clearSelection();
    this.api.setSelectedAriShardPath(null);
    if (storeId === 'ari') {
      return;
    }
    this.api.loadDatabases(storeId);
    this.api.loadStoreStats(storeId);
  }

  onAriNodeChange(event: Event): void {
    const el = event.target as HTMLSelectElement;
    const value = el?.value ?? '';
    this.selectedDbName.set(null);
    this.selectedKey.set(null);
    this.api.setSelectedAriShardPath(value || null);
    this.api.clearSelection();
    if (value) {
      this.api.loadDatabases('ari');
      this.api.loadStoreStats('ari');
    }
  }

  selectDb(dbName: string): void {
    this.selectedDbName.set(dbName);
    this.selectedKey.set(null);
    this.api.loadKeys(
      this.selectedStoreId()!,
      dbName,
      this.keyPrefix() || undefined,
      this.keysLimit,
      this.keysOffset(),
    );
  }

  selectKey(key: string): void {
    this.selectedKey.set(key);
    this.api.loadValue(
      this.selectedStoreId()!,
      this.selectedDbName()!,
      key,
    );
  }

  onPrefixChange(prefix: string): void {
    this.keyPrefix.set(prefix);
    const storeId: string = this.selectedStoreId();
    const dbName: string = this.selectedDbName();
    if (storeId && dbName) {
      this.keysOffset.set(0);
      this.api.loadKeys(storeId, dbName, prefix || undefined, this.keysLimit, 0);
    }
  }

  loadMoreKeys(): void {
    const storeId: string = this.selectedStoreId();
    const dbName: string = this.selectedDbName();
    if (!storeId || !dbName) return;
    const next: number = this.keysOffset() + this.keysLimit;
    this.keysOffset.set(next);
    this.api.loadKeys(storeId, dbName, this.keyPrefix() || undefined, this.keysLimit, next);
  }

  refreshStores(): void {
    this.api.loadStores();
    this.api.loadStatistics();
  }

  refreshStatistics(): void {
    this.api.loadStatistics();
  }

  async copySelectedValue(): Promise<void> {
    try {
      const text = this.selectedValueDisplay();
      if (!text) {
        return;
      }
      await navigator.clipboard.writeText(text);
      await this.showToast('Copied to clipboard');
    } catch {
      await this.showToast('Failed to copy to clipboard', true);
    }
  }

  async confirmDeleteKey(key: string, event?: Event): Promise<void> {
    if (event) event.stopPropagation();
    const storeId: string = this.selectedStoreId();
    const dbName: string = this.selectedDbName();
    if (!storeId || !dbName) return;
    const alert = await this.alertController.create({
      header: 'Delete Key',
      subHeader: key,
      message: 'Are you sure you want to delete this key? This cannot be undone.',
      cssClass: 'delete-key-alert',
      buttons: [
        {text: 'Cancel', role: 'cancel'},
        {
          text: 'Delete',
          role: 'destructive',
          handler: () => {
            this.api.deleteKey(storeId, dbName, key).subscribe({
              next: (res) => {
                if (res?.deleted) {
                  this.selectedKey.set(null);
                  this.api.loadKeys(storeId, dbName, this.keyPrefix() || undefined, this.keysLimit, this.keysOffset());
                  this.api.loadStoreStats(storeId);
                  this.api.loadDatabases(storeId);
                  this.api.loadStatistics();
                  this.showToast('Key deleted successfully');
                } else {
                  this.showToast(res?.error || 'Delete failed', true);
                }
              },
              error: (err) => this.showToast(err?.message || 'Delete failed', true),
            });
          },
        },
      ],
    });
    await alert.present();
  }

  async confirmDeleteAllKeys(event?: Event): Promise<void> {
    if (event) event.stopPropagation();
    const count: number = this.selectedDbKeyCount();
    const dbName: string = this.selectedDbName();
    const storeId: string = this.selectedStoreId();
    if (!dbName || !storeId) return;
    const alert = await this.alertController.create({
      header: 'Delete All Keys',
      subHeader: `${this.formatCount(count)} keys in ${dbName}`,
      message: 'Are you sure you want to delete all keys in this database? This action is permanent and cannot be undone.',
      cssClass: 'delete-key-alert',
      buttons: [
        {text: 'Cancel', role: 'cancel'},
        {
          text: 'Delete All',
          role: 'destructive',
          handler: () => {
            this.api.deleteAllKeys(storeId, dbName).subscribe({
              next: (res) => {
                const result = res as { deleted?: number; error?: string };
                if (result?.deleted !== undefined) {
                  this.selectedKey.set(null);
                  this.api.loadKeys(storeId, dbName, this.keyPrefix() || undefined, this.keysLimit, 0);
                  this.api.loadStoreStats(storeId);
                  this.api.loadDatabases(storeId);
                  this.api.loadStatistics();
                  this.showToast('All keys deleted successfully');
                } else {
                  this.showToast(result?.error || 'Delete failed', true);
                }
              },
              error: (err) => this.showToast(err?.message || 'Delete failed', true),
            });
          },
        },
      ],
    });
    await alert.present();
  }

  /**
   * Start SEO URL cache warm for a website-cache DB.
   * mobile_app requires platform + version (prompt if missing from DB name).
   */
  async confirmStartWebsiteCache(
    config: WebsiteCacheStartConfigBody,
    event?: Event,
  ): Promise<void> {
    if (event) {
      event.stopPropagation();
    }
    if (this.startingCache()) {
      return;
    }

    let body: WebsiteCacheStartConfigBody = {...config};

    if (body.cacheMode === 'mobile_app') {
      if (!body.platform) {
        const platform = await this.promptMobileAppPlatform();
        if (!platform) {
          return;
        }
        body = {...body, platform};
      }
      if (!body.version?.trim()) {
        const version = await this.promptMobileAppVersion();
        if (!version) {
          return;
        }
        body = {...body, version};
      }
    }

    await this.presentStartCacheConfirm(body);
  }

  private promptMobileAppPlatform(): Promise<'ios' | 'android' | null> {
    return new Promise(async (resolve) => {
      const alert = await this.alertController.create({
        header: 'Select platform',
        inputs: [
          {type: 'radio', label: 'iOS', value: 'ios', checked: true},
          {type: 'radio', label: 'Android', value: 'android'},
        ],
        buttons: [
          {text: 'Cancel', role: 'cancel', handler: () => resolve(null)},
          {
            text: 'Continue',
            handler: (value: 'ios' | 'android') => {
              resolve(value === 'ios' || value === 'android' ? value : null);
            },
          },
        ],
      });
      await alert.present();
    });
  }

  private promptMobileAppVersion(): Promise<string | null> {
    return new Promise(async (resolve) => {
      const alert = await this.alertController.create({
        header: 'App version',
        message: 'Enter version (e.g. 1.0.70 or 8.50.81)',
        inputs: [
          {
            name: 'version',
            type: 'text',
            placeholder: 'version',
          },
        ],
        buttons: [
          {text: 'Cancel', role: 'cancel', handler: () => resolve(null)},
          {
            text: 'Continue',
            handler: (data: {version?: string}) => {
              const version = String(data?.version ?? '').trim();
              if (!version) {
                this.showToast('Enter app version', true);
                resolve(null);
                return false;
              }
              resolve(version);
              return true;
            },
          },
        ],
      });
      await alert.present();
    });
  }

  private async presentStartCacheConfirm(
    body: WebsiteCacheStartConfigBody,
  ): Promise<void> {
    const label = formatWebsiteCacheConfigLabel(body);
    const alert = await this.alertController.create({
      header: 'Start SEO URL Cache',
      subHeader: label,
      message:
        'This starts a config cache job for all SEO URLs (same JSON path as cron). Same config cannot run twice while PROCESSING.',
      buttons: [
        {text: 'Cancel', role: 'cancel'},
        {
          text: 'Start Cache',
          handler: () => {
            this.startWebsiteCacheJob(body);
          },
        },
      ],
    });
    await alert.present();
  }

  private startWebsiteCacheJob(body: WebsiteCacheStartConfigBody): void {
    this.startingCache.set(true);
    // Start Cache uses the normal dashboard gateway (environment.restAPI),
    // not LMDB "Select the Server". Browse/clear stay on the selected server.
    this.httpClientService
      .post('seo-url/cache-cron/start-config', body, {}, false)
      .subscribe({
        next: (res: {
          success?: boolean;
          message?: string;
          reportJobId?: string;
        }) => {
          this.startingCache.set(false);
          if (res?.success) {
            const id = res.reportJobId ? ` (job ${res.reportJobId})` : '';
            this.showToast(`${res.message || 'Cache job started'}${id}`);
          } else {
            this.showToast(res?.message || 'Failed to start cache job', true);
          }
        },
        error: (err) => {
          this.startingCache.set(false);
          this.showToast(
            err?.error?.message || err?.message || 'Failed to start cache job',
            true,
          );
        },
      });
  }

  private async showToast(message: string, isError = false): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration: 3000,
      color: isError ? 'danger' : 'success',
    });
    await toast.present();
  }
}
